import { ApiClient } from '../../../rest-api/fetsClient';
import { throwIfNotOk } from '../../../rest-api/throwIfNotOk';
import type { CourseExportLink } from './courseExportLinksApi';
import { TagColor } from '../../../types/Tags';
import { createAllowedDomainsApi } from './allowedDomainsApi';
import { createCourseExportVersionApi } from './courseExportDetailApi';
import { createCourseExportLinksApi } from './courseExportLinksApi';
import { createCourseExportModuleApi } from './courseExportModuleApi';
import { createCourseExportVersionsApi } from './courseExportVersionsApi';
import { createGetProjectCourseExports } from './getProjectCourseExportsApi';
import { createPublishExportVersionApi } from './publishExportVersionApi';

type SemanticVersion = { major: number; minor: number; patch: number };
type CourseExportTag = { guid: string; name: string; color: TagColor };
type CourseExportListingItem = {
  guid: string;
  createdAtTimestamp: number;
  version: SemanticVersion;
  name: string;
  publishedAtTimestamp: number;
  tags: CourseExportTag[];
  links: CourseExportLink[];
};

export const createCourseExportClient = (apiClient: ApiClient) => {
  const listCourseExports = async (): Promise<CourseExportListingItem[]> => {
    const response = await apiClient['/api/course-exports/listing'].get();
    const data = await response.json();
    return data.map((item) => {
      const result: CourseExportListingItem = {
        guid: item.guid,
        createdAtTimestamp: item.createdAtTimestamp,
        version: item.version,
        name: item.name,
        publishedAtTimestamp: item.publishedAtTimestamp,
        tags: item.tags.map((tag) => ({ guid: tag.guid, name: tag.name, color: tag.color })),
        links: item.links,
      };
      return result;
    });
  };

  const createCourseExport = async (name: string): Promise<string> => {
    const response = await apiClient['/api/course-exports/create'].post({
      json: {
        name,
      },
    });
    const data = await response.json();
    return data.guid;
  };

  const addCourseExportVersion = async (params: {
    courseExportGuid: string;
    file: File;
    version: SemanticVersion;
    description: string;
    changelog: string | null;
    publishWhenProcessed: boolean;
  }): Promise<string> => {
    const formData = {
      file: params.file,
      courseExportGuid: params.courseExportGuid,
      // Dot notation binds the nested SemanticVersion on the server.
      'version.major': params.version.major,
      'version.minor': params.version.minor,
      'version.patch': params.version.patch,
      description: params.description,
      publishWhenProcessed: params.publishWhenProcessed,
      ...(params.changelog ? { changelog: params.changelog } : {}),
    };

    const response = await apiClient['/api/course-exports/add-version'].post({ formData });
    await throwIfNotOk(response, 'create the version');
    return await response.text();
  };

  return {
    listCourseExports,
    createCourseExport,
    getCourseExportDetail: createCourseExportVersionApi(apiClient),
    publishExportVersion: createPublishExportVersionApi(apiClient),
    getProjectCourseExports: createGetProjectCourseExports(apiClient),
    addCourseExportVersion,
    ...createAllowedDomainsApi(apiClient),
    ...createCourseExportLinksApi(apiClient),
    ...createCourseExportModuleApi(apiClient),
    ...createCourseExportVersionsApi(apiClient),
  };
};
